import * as ColorUtil from "../utils/ColorUtil"
import CSS from 'csstype'
import { PresetJson, PresetImage, BackgroundColorFill, BackgroundColorFillGradient, BackgroundColorFillSolid, GradientStyle, ColorFillType } from "./Preset"
import { ReactNode } from "react"

export type PropsWithTheme<P> = P & { theme: Theme }

export default class Theme {
  textColor: string
  primaryBackgroundColor: string
  secondaryBackgroundColor: string
  highestContrastBackgroundColor: string
  headerImage: PresetImage | null
  headerImageUrl: string | null
  mode: string
  headerMaxHeight: string
  heading: string
  subheading: string
  firstOverlayUrl: string | null

  textStyle: CSS.Properties
  svgStyle: CSS.Properties
  svgWithStrokeStyle: CSS.Properties
  svgStyleAlternate: CSS.Properties
  buttonStyle: CSS.Properties
  inverseButtonStyle: CSS.Properties
  bigButtonStyle: CSS.Properties
  secondaryBigButtonStyle: CSS.Properties
  disclaimerStyle: CSS.Properties
  borderStyle: CSS.Properties
  iconStyle: CSS.Properties
  backgroundStyle: CSS.Properties
  backgroundImageStyle: CSS.Properties
  toolbarStyle: CSS.Properties
  toolbarSelectedImageStyle: CSS.Properties
  inactiveTabStyles: CSS.Properties[]
  previewStyle: CSS.Properties

  poweredBy: { entity: string, url: string }
  customLegal?: ReactNode

  constructor(preset: PresetJson) {
    this.textColor = preset.textColor

    let backgroundColors = this.parseBackgroundColors(preset._embedded.backgroundColorFill)
    this.primaryBackgroundColor = backgroundColors.first
    this.secondaryBackgroundColor = backgroundColors.second

    this.highestContrastBackgroundColor = ColorUtil.pickHigherContrastOption(this.primaryBackgroundColor, this.secondaryBackgroundColor, this.textColor)
    this.headerImage = preset._embedded.headerImage ?? null
    this.headerImageUrl = null

    let overlays = preset._embedded.overlays

    this.mode = ColorUtil.isBrighter(this.highestContrastBackgroundColor, this.textColor) ? "light" : "dark"

    this.headerMaxHeight = "10vh"
    this.configureHeaderHeight()

    this.heading = preset.heading
    this.subheading = preset.subheading
    this.firstOverlayUrl = overlays.length > 0 ? (overlays[0].url + "?blameChrome") : null
    this.textStyle = { color: this.textColor }
    this.svgStyle = { fill: this.highestContrastBackgroundColor }
    this.svgWithStrokeStyle = {
      fill: this.highestContrastBackgroundColor,
      stroke: this.highestContrastBackgroundColor
    }
    this.svgStyleAlternate = { fill: this.textColor }
    this.buttonStyle = {
      backgroundColor: this.textColor,
      color: this.highestContrastBackgroundColor,
    }
    this.inverseButtonStyle = {
      backgroundColor: this.primaryBackgroundColor === this.secondaryBackgroundColor ? ColorUtil.brightenHexColor(this.highestContrastBackgroundColor, -40) : this.highestContrastBackgroundColor,
      color: this.textColor
    }
    this.bigButtonStyle = {
      color: this.highestContrastBackgroundColor,
      backgroundColor: this.textColor,
      boxShadow: "0px 5px 0px " + ColorUtil.hexToCssRgba(this.textColor, 0.7),
    }
    this.secondaryBigButtonStyle = {
      color: this.textColor,
      border: "3px solid " + this.textColor
    }
    this.disclaimerStyle = {
      color: this.textColor,
      borderColor: this.textColor
    }
    this.borderStyle = { borderColor: this.textColor }
    this.iconStyle = { backgroundColor: this.primaryBackgroundColor }
    this.backgroundStyle = { backgroundImage: this.getCssGradientSyntax(preset._embedded.backgroundColorFill.style) }
    this.backgroundImageStyle = {}
    this.toolbarStyle = {
      backgroundColor: ColorUtil.mixHexColors(this.highestContrastBackgroundColor, this.textColor, 0.2)
    }
    this.toolbarSelectedImageStyle = { boxShadow: "0 0 0 3px " + this.primaryBackgroundColor }
    this.inactiveTabStyles = [
      {
        backgroundColor: ColorUtil.mixHexColors(this.highestContrastBackgroundColor, this.textColor, 0.15)
      },
      {
        backgroundColor: ColorUtil.mixHexColors(this.highestContrastBackgroundColor, this.textColor, 0.10)
      }
    ]
    this.previewStyle = { backgroundColor: this.textColor }

    if (preset._embedded.backgroundImage) {
      let image = preset._embedded.backgroundImage
      this.backgroundImageStyle = {
        background: `url(${image.url}?blameChrome) no-repeat center fixed, ${this.getCssGradientSyntax(preset._embedded.backgroundColorFill.style)}`,
        backgroundSize: "cover",
        opacity: image.opacity
      }
      if (image.fit === "TILE") {
        this.backgroundImageStyle.backgroundRepeat = "repeat"
        this.backgroundImageStyle.backgroundSize = "auto"
      }
    }

    this.poweredBy = {
      entity: "Simple Booth",
      url: "https://www.simplebooth.com/virtual-photo-booth"
    }

    this.configureCustomizations(preset.code)
  }

  parseBackgroundColors(backgroundColorFill: BackgroundColorFill): { first: string, second: string } {
    if (backgroundColorFill.type === ColorFillType.SOLID) {
      let solidFill = backgroundColorFill as BackgroundColorFillSolid
      return { first: solidFill.color, second: solidFill.color }
    }
    let gradientFill = backgroundColorFill as BackgroundColorFillGradient
    return { first: gradientFill.firstColor, second: gradientFill.secondColor }
  }

  getCssGradientSyntax(gradientStyle: GradientStyle): string {
    if (gradientStyle === GradientStyle.RADIAL) {
      return "radial-gradient(" + this.primaryBackgroundColor + ", " + this.secondaryBackgroundColor + ")"
    }
    return "linear-gradient(180deg, " + this.primaryBackgroundColor + " 0%, " + this.secondaryBackgroundColor + " 80%)"
  }

  configureCustomizations(presetCode: string) {
    // Major Biden's Indoguration
    if (presetCode === "SMymDakDy99j") {
      this.headerMaxHeight = "25vh"
    }
    // Lowe's MOTY
    if (presetCode === "JV3FY3CXyMc4") {
      this.headerMaxHeight = "25vh"
    }
  }

  configureHeaderHeight() {
    if (this.headerImage && this.headerImage.height) {
      let pixelsTall = this.headerImage.height
      let maxHeight = pixelsTall / 18.0
      let maxHeightConstrained = Math.min(Math.max(maxHeight, 10), 25)
      this.headerMaxHeight = maxHeightConstrained + "vh"
    }
  }
}